import { call } from '@advanza/api'
import { hasAccess } from 'misc/user'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    subjectId,
    VerificationStatus,
    VerificationSubject,
    VerificationSubjectType,
} from './subjects'

export interface VerificationRow {
    design_system_verification_id: number
    subject_type: VerificationSubjectType
    subject_key: string
    version: string
    source_hash: string
    status: VerificationStatus
    reviewed_at: string | null
    reviewed_by_name: string | null
    comment_count: number
}

// `stale` is not a stored status: it is an approval whose fingerprint no longer
// matches what the page renders, i.e. the design moved after sign-off.
export type VerificationState =
    | 'unreviewed'
    | 'pending'
    | 'approved'
    | 'stale'
    | 'changes_requested'

interface VerificationContextValue {
    getRow: (subject: VerificationSubject) => VerificationRow | undefined
    getState: (subject: VerificationSubject) => VerificationState
    applySaved: (row: VerificationRow) => void
    canWrite: boolean
    loading: boolean
    error: string | null
}

const VerificationContext = createContext<VerificationContextValue | null>(null)

/**
 * Loads every sign-off row once and exposes it by subject. A context rather than
 * a hook per badge because the Foundations page mounts hundreds of badges and
 * they all read from the same single request.
 */
export const VerificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [rows, setRows] = useState<Map<string, VerificationRow>>(new Map())
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false

        call('office/design-system-verifications/get-list')
            .then((response: { items: VerificationRow[] }) => {
                if (cancelled) {
                    return
                }
                setRows(new Map(response.items.map((item) => [subjectId(rowSubject(item)), item])))
                setLoading(false)
            })
            .catch(() => {
                if (cancelled) {
                    return
                }
                setError('Could not load verification statuses')
                setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [])

    const getRow = useCallback(
        (subject: VerificationSubject) => rows.get(subjectId(subject)),
        [rows],
    )

    const getState = useCallback(
        (subject: VerificationSubject): VerificationState => {
            const row = rows.get(subjectId(subject))

            if (!row) {
                return 'unreviewed'
            }
            if (row.status === 'approved' && row.source_hash !== subject.hash) {
                return 'stale'
            }
            return row.status
        },
        [rows],
    )

    const applySaved = useCallback((row: VerificationRow) => {
        setRows((previous) => new Map(previous).set(subjectId(rowSubject(row)), row))
    }, [])

    const value = useMemo(
        () => ({
            getRow,
            getState,
            applySaved,
            canWrite: Boolean(hasAccess('design_system', 'w')),
            loading,
            error,
        }),
        [getRow, getState, applySaved, loading, error],
    )

    return <VerificationContext.Provider value={value}>{children}</VerificationContext.Provider>
}

export function useVerifications(): VerificationContextValue {
    const context = useContext(VerificationContext)

    if (!context) {
        throw new Error('useVerifications must be used inside a VerificationProvider')
    }

    return context
}

function rowSubject(row: VerificationRow) {
    return { type: row.subject_type, key: row.subject_key, version: row.version }
}
